import { Currency } from '~/entities/currency';
import {
    type PayoutTraderWorkingStatus,
    PayoutTraderStatus,
    useActivatePayoutTrader,
    useBlockPayoutTrader,
    usePayoutTrader,
    usePayoutTraderBalance,
    usePayoutTraderRate,
    useSetPayoutTraderWorkingStatus,
    useUnBlockPayoutTrader,
    useUpdatePayoutTraderConfig,
    useUpdatePayoutTraderRate,
} from '~/entities/payout';
import { type ActivateTraderRequest, type UpdateTraderConfigRequest, type UpdateTraderRateRequest } from '../../model';
import { prepareBalance } from '../utils';

const traderBalancePollingInterval = 5000;

export const useTraderEditor = (id: string) => {
    const traderRes = usePayoutTrader({ 'traderId.value': id });
    const trader = traderRes.data?.trader;

    const editTraderConfig = useUpdatePayoutTraderConfig();
    const setWorkingStatus = useSetPayoutTraderWorkingStatus();
    const block = useBlockPayoutTrader();
    const unBlock = useUnBlockPayoutTrader();
    const activate = useActivatePayoutTrader();
    const editTraderRate = useUpdatePayoutTraderRate();

    const traderId = trader?.id?.value;
    const effectiveTraderId = traderId ?? id;
    const isTraderBalanceAvailable = Boolean(traderId) && trader?.status !== PayoutTraderStatus.STATUS_CREATED;
    const isTraderRateAvailable =
        Boolean(effectiveTraderId) && Boolean(trader) && trader?.status !== PayoutTraderStatus.STATUS_CREATED;
    const traderBalance = usePayoutTraderBalance(traderId ? { 'traderId.value': traderId } : undefined, {
        enabled: isTraderBalanceAvailable,
        refetchInterval: traderBalancePollingInterval,
    });
    const traderRate = usePayoutTraderRate(
        isTraderRateAvailable ? { 'traderId.value': effectiveTraderId } : undefined,
        {
            enabled: isTraderRateAvailable,
        },
    );

    const handleUpdateConfig = (values: UpdateTraderConfigRequest) => {
        editTraderConfig.mutate(values);
    };

    const handleUpdateRate = (values: UpdateTraderRateRequest) => {
        editTraderRate.mutate(values);
    };

    const handleReloadRate = () => {
        void traderRate.refetch();
    };

    const handleSetWorkingStatus = (workingStatus: PayoutTraderWorkingStatus) => {
        if (!traderId) {
            return;
        }

        setWorkingStatus.mutate({
            traderId: {
                value: traderId,
            },
            workingStatus,
        });
    };

    const handleBlock = () => {
        if (!traderId) {
            return;
        }

        block.mutate({
            traderId: {
                value: traderId,
            },
        });
    };

    const handleUnBlock = () => {
        if (!traderId) {
            return;
        }

        unBlock.mutate({
            traderId: {
                value: traderId,
            },
        });
    };

    const handleActivate = (values: ActivateTraderRequest) => {
        activate.mutate(values);
    };

    return {
        activate,
        block,
        canEditWorkingStatus: trader?.status === PayoutTraderStatus.STATUS_ACTIVE && Boolean(traderId),
        configFormKey: trader ? `${traderId ?? id}:${JSON.stringify(trader.config)}` : undefined,
        editTraderRate,
        editTraderConfig,
        handleActivate,
        handleBlock,
        handleReloadRate,
        handleSetWorkingStatus,
        handleUnBlock,
        handleUpdateConfig,
        handleUpdateRate,
        isTraderBalanceAvailable,
        isTraderBalanceLoading: isTraderBalanceAvailable && traderBalance.isPending,
        isTraderLoading: traderRes.isPending,
        isTraderRateError: isTraderRateAvailable && traderRate.isError,
        isTraderRateLoading: isTraderRateAvailable && traderRate.isFetching,
        requestedTraderId: id,
        traderRate: traderRate.data,
        rateFormKey: traderRate.data
            ? `${effectiveTraderId}:${traderRate.data.commission?.value ?? ''}:${traderRate.data.calculationRule ?? ''}`
            : undefined,
        rubTraderBalance: prepareBalance(traderBalance.data?.balances, Currency.CURRENCY_RUB),
        setWorkingStatus,
        trader,
        traderDisplayName: trader?.traderName?.value ?? trader?.username ?? id,
        traderId,
        unBlock,
    };
};
